import {
  call, put, all, select, delay,
} from 'redux-saga/effects';
import {
  apiGetTasks,
  apiGetTasksFilters,
  apiGetTaskDetail,
  apiGetTaskNfs,
  apiGetTaskPaymentTicket,
  apiGetAddress,
  apiHireTask,
  apiCancelHiredTask,
  apiGetCancelTaskOptions,
  apiCancelQuoteTask,
  getReportProblemReasonsApi,
  sendReportProblemApi,
  getReportProblemsApi,
  apiAddPhotoToTask,
  apiRemovePhotoFromTask,
  apiRatingTaskProfessionalRequest,
  apiAuthorizeTask,
  apiScheduling,
  apiCreatePaymentInfo,
  apiUpdatePaymentInfo,
  getStatusTabs,
  getTasksByCompanyAndTab,
  updateTaskVisited,
  editTaskVisit,
  createTaskVisit,
  deleteTaskVisit,
  apiGetComments,
  apiIncludeComment,
  linkUserOperator,
  unlinkUserOperator,
  generateCheckoutLink,
} from 'services/api/task';

import { createTaskLabel, deleteTaskLabel } from 'services/api/labels';

import { createUser, updateUser } from 'services/api/users';

import { getErrorMessage } from 'utils/filters';

import { PaymentMethods } from 'config/shared';
import { Creators as TasksCreators } from '../ducks/task';
import { Creators as ChatCreators } from '../ducks/chat';
import { Creators as UiCreators, growlTypes } from '../ducks/ui';
import { getCompany, getTriiderUser } from '../ducks/auth';
import { Creators as LocalCreators } from '../ducks/locals';


const fileName = 'sagas/task';

export function* getTasks({ payload }) {
  try {
    yield put(UiCreators.showLoader('TASK_LIST'));
    const company = yield select(getCompany);

    const filters = payload
      .filter(f => !!f.value || f.name === 'offset')
      .map(({ name, value }) => `${name}=${value}`)
      .join('&');

    const { tasks, total } = yield call(apiGetTasks, company.id, filters);

    yield put(TasksCreators.getTasksSuccess({ tasks, total }));
    yield all([
      put(TasksCreators.setActiveFilters(payload)),
      put(UiCreators.hideLoader('TASK_LIST')),
    ]);
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getTasks`,
        }),
      ),
      put(UiCreators.hideLoader('TASK_LIST')),
    ]);
  }
}

export function* getTaskByStatus({ payload }) {
  try {
    const {
      id, query, listFilters, offset,
    } = payload;
    yield put(UiCreators.showLoader('TASK_LIST'));
    const company = yield select(getCompany);

    let queryFinal;
    let tab;
    switch (id) {
      case 1:
        tab = 'open';
        break;
      case 2:
        tab = 'hired';
        break;
      case 3:
        tab = 'charged';
        break;
      case 4:
        tab = 'finished';
        break;
      default:
        tab = 'open';
        break;
    }
    yield put(TasksCreators.setStatusSuccess({ id, value: tab, offset: offset || 0 }));

    queryFinal = query || '';
    // adding filters on queryFinal
    if (listFilters && listFilters.length > 0) {
      listFilters.forEach((filter, index) => {
        if (`${filter.value}` !== '') {
          if (queryFinal === '' && index === 0) {
            queryFinal = `${filter.query}=${filter.value}`;
          } else {
            queryFinal += `&${filter.query}=${filter.value}`;
          }
        }
      });
    }

    const { tasks, total, filters } = yield call(
      getTasksByCompanyAndTab,
      company.id,
      tab,
      queryFinal,
    );

    yield put(TasksCreators.getTasksSuccess({ tasks, total, filters }));
    if (offset !== undefined && offset !== null) {
      yield put(
        TasksCreators.setActiveFilters([{ name: 'offset', value: offset }]),
      );
    }
    yield put(UiCreators.hideLoader('TASK_LIST'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/setTaskByStatus`,
        }),
      ),
      put(UiCreators.hideLoader('TASK_LIST')),
    ]);
  }
}

export function* getTabsStatus() {
  try {
    const company = yield select(getCompany);
    const data = yield call(getStatusTabs, company.id);

    yield put(TasksCreators.getTabsStatusSuccess(data.tabsStatus));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getStatusTabs`,
        }),
      ),
      put(UiCreators.hideLoader('STATUS_TAB')),
    ]);
  }
}

export function* getTasksFilters({ payload }) {
  try {
    const {
      key, term, url, setUp,
    } = payload;

    if (!setUp) {
      yield delay(500);
    }

    const company = yield select(getCompany);

    const options = yield call(apiGetTasksFilters, url, company.id, term);

    yield put(TasksCreators.getTasksFiltersSuccess({ key, options }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getTasksFilters`,
        }),
      ),
    ]);
  }
}

export function* getTaskDetail({ payload }) {
  try {
    yield put(UiCreators.showLoader('TASK_DETAIL'));
    const company = yield select(getCompany);

    const { task } = yield call(apiGetTaskDetail, company.id, payload);
    const { nfs } = yield call(apiGetTaskNfs, task.id, payload);
    const { list } = yield call(apiGetTaskPaymentTicket, task.id, payload);
    const { address } = yield call(
      apiGetAddress,
      company.id,
      task.address_id,
      payload,
    );

    yield all([
      put(
        TasksCreators.getTaskDetailSuccess({
          ...task,
          taskNfs: nfs,
          taskPaymentTicket: list,
          taskAddress: address,
        }),
      ),
      put(UiCreators.hideLoader('TASK_DETAIL')),
      put(TasksCreators.hireTaskSuccess(false)),
    ]);
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getTaskDetail`,
        }),
      ),
      put(UiCreators.hideLoader('TASK_DETAIL')),
    ]);
  }
}

export function* hireTask({ payload }) {
  try {
    yield put(UiCreators.showLoader('HIRE_TASK'));

    const { taskId, quoteId, generateCheckoutLink } = payload;

    const hired = yield call(apiHireTask, taskId, quoteId);
    yield put(TasksCreators.hireTaskSuccess(hired.success));

    const chat_id = yield select(
      state => state.task.taskQuoteProDetail.chat_id,
    );

    const message = {
      chat_id,
      text:
        'Você contratou esse profissional! Não esqueça de marcar o dia e horário do atendimento por aqui!',
      time: new Date(),
      is_read: false,
      is_system: true,
      system_key: 'pro_hired',
    };

    yield put(ChatCreators.sendMessage([message]));

    if (generateCheckoutLink) {
      yield call(generateCheckoutLinkSaga, { payload: { task_id: taskId, quote_id: quoteId, ...payload } });
    }

    yield put(UiCreators.hideLoader('HIRE_TASK'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/hireTask`,
        }),
      ),
      put(UiCreators.hideLoader('HIRE_TASK')),
    ]);
  }
}

export function* getCancelTaskOptions({ payload }) {
  try {
    const { status } = payload;
    const { list } = yield call(apiGetCancelTaskOptions, status);

    yield put(TasksCreators.getCancelTaskOptionsSuccess(list));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/hireTask`,
        }),
      ),
      // put(UiCreators.hideLoader()),
    ]);
  }
}

export function* cancelQuoteTask({ payload }) {
  const { quoteId } = payload;
  try {
    // yield put(UiCreators.showLoader());

    const canceled = yield call(apiCancelQuoteTask, quoteId);

    yield put(TasksCreators.setQuoteCanceledSuccess(canceled.success));

    // yield put(UiCreators.hideLoader())
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/cancelHiredTask`,
        }),
      ),
      // put(UiCreators.hideLoader()),
    ]);
  }
}

export function* cancelHiredTask({ payload }) {
  try {
    // yield put(UiCreators.showLoader());

    const canceled = yield call(apiCancelHiredTask, payload);

    yield put(TasksCreators.cancelHiredTaskSuccess(canceled.success));

    // yield put(UiCreators.hideLoader())
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/cancelHiredTask`,
        }),
      ),
      // put(UiCreators.hideLoader()),
    ]);
  }
}

export function* getReportProblemReasons({ payload }) {
  try {
    const { list = [] } = yield call(getReportProblemReasonsApi, payload);

    yield put(TasksCreators.reportProblemReasonsSuccess(list));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getReportProblemReasons`,
        }),
      ),
      // put(UiCreators.hideLoader()),
    ]);
  }
}

export function* reportProblem({ payload }) {
  try {
    yield put(UiCreators.showLoader('REPORT_PROBLEM'));

    const { task_id, feedback } = payload;
    const {
      user: { id: user_id },
    } = yield select(getTriiderUser);

    const { taskProblem } = yield call(
      sendReportProblemApi,
      task_id,
      user_id,
      feedback,
    );

    const oldProblems = yield select(
      state => state.task.reportProblem.problems,
    );

    const problems = [
      ...oldProblems,
      {
        ...taskProblem,
        feedback_text: taskProblem.feedback_text || taskProblem.text,
      },
    ];

    yield put(TasksCreators.reportProblemSuccess({ problems, success: true }));
    yield all([
      put(UiCreators.growl('Problema reportado!', growlTypes.GROWL_SUCCESS)),
      put(UiCreators.hideLoader('REPORT_PROBLEM')),
    ]);
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/reportProblem`,
        }),
      ),
      put(UiCreators.hideLoader('REPORT_PROBLEM')),
    ]);
  }
}

export function* getReportProblems({ payload: task_id }) {
  try {
    yield put(UiCreators.showLoader('REPORT_PROBLEM'));

    const { problems } = yield call(getReportProblemsApi, task_id);

    yield put(TasksCreators.getReportProblemsSuccess(problems));
    yield put(UiCreators.hideLoader('REPORT_PROBLEM'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/getReportProblems`,
        }),
      ),
      put(UiCreators.hideLoader('REPORT_PROBLEM')),
    ]);
  }
}

export function* addPhotosToTask({ payload }) {
  try {
    yield put(UiCreators.showLoader('TASK_ADD_PHOTO'));

    const { taskId, photos } = payload;

    const { data } = yield all(
      photos.map(photo => call(apiAddPhotoToTask, taskId, photo)),
    );
    yield put(TasksCreators.getTaskDetailRequest(taskId));
    yield put(
      UiCreators.growl(
        photos.length > 1
          ? 'Fotos adicionadas com sucesso!'
          : 'Foto adicionada com sucesso!',
        growlTypes.GROWL_SUCCESS,
      ),
    );

    yield put(UiCreators.hideLoader('TASK_ADD_PHOTO'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/addPhotosToTask`,
        }),
      ),
      put(UiCreators.hideLoader('TASK_ADD_PHOTO')),
    ]);
  }
}

export function* removePhotosFromTask({ payload }) {
  try {
    // yield put(UiCreators.showLoader());

    const { task_id: taskId, id: photoId } = payload;

    const data = yield call(apiRemovePhotoFromTask, photoId);
    yield put(TasksCreators.getTaskDetailRequest(taskId));
    yield put(
      UiCreators.growl('Foto deletada com sucesso!', growlTypes.GROWL_SUCCESS),
    );

    // yield put(UiCreators.hideLoader())
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/addPhotosToTask`,
        }),
      ),
      // put(UiCreators.hideLoader()),
    ]);
  }
}

export function* ratingTaskProfessionalRequest({ payload }) {
  try {
    yield put(UiCreators.showLoader('RATING_TASK'));

    const { task_id, feedback } = payload;

    yield call(apiRatingTaskProfessionalRequest, { ...feedback, task_id });

    yield put(TasksCreators.ratingTaskProfessionalSuccess());

    yield put(
      UiCreators.growl('Obrigado por Avaliar!', growlTypes.GROWL_SUCCESS),
    );

    yield put(UiCreators.hideLoader('RATING_TASK'));
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/ratingTaskProfessionalRequest`,
        }),
      ),
      yield put(UiCreators.hideLoader('RATING_TASK')),
    ]);
  }
}

export function* authorizeTaskRequest({ payload }) {
  try {
    yield put(UiCreators.showLoader('AUTHORIZE_TASK_SUCCESS'));

    const { quoteId, success } = payload;

    yield call(apiAuthorizeTask, { quoteId, success });

    yield put(TasksCreators.authorizeTaskSuccess(payload));

    yield put(
      UiCreators.growl(
        'Pedido autorizado com sucesso!',
        growlTypes.GROWL_SUCCESS,
      ),
    );

    yield put(UiCreators.hideLoader('AUTHORIZE_TASK_SUCCESS'));
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/authorizeTaskRequest`,
        }),
      ),
      put(UiCreators.hideLoader('AUTHORIZE_TASK_SUCCESS')),
    ]);
  }
}

export function* scheduling({ payload }) {
  try {
    yield put(UiCreators.showLoader('SCHEDULING_REQUEST'));

    yield call(apiScheduling, payload);

    yield put(TasksCreators.schedulingSuccess(payload));

    yield put(TasksCreators.hireTaskSuccess(true));

    yield put(UiCreators.hideLoader('SCHEDULING_REQUEST'));
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/schedulingRequest`,
        }),
      ),
      put(UiCreators.hideLoader('SCHEDULING_REQUEST')),
    ]);
  }
}

export function* paymentInfo({ payload }) {
  try {
    yield put(UiCreators.showLoader('PAYMENT_INFO'));

    const { task_id, from_type } = payload;
    let apiPaymentInfo;

    yield put(TasksCreators.setLoading(true));

    if (payload.update) {
      apiPaymentInfo = yield call(apiUpdatePaymentInfo, task_id, payload);
    } else {
      apiPaymentInfo = yield call(apiCreatePaymentInfo, task_id, payload);
    }

    if (payload.paymentMethodSelected) {
      yield call(apiIncludeComment, task_id, {
        type: 'paymentNote',
        user_id: payload.userId,
        description: `Método de pagamento ${payload.paymentMethodSelected === PaymentMethods.DOC
          ? 'DOC' : 'Cartão de Crédito'} selecionado`,
      });
    }

    if (payload.notes) {
      yield call(apiIncludeComment, task_id, {
        type: 'paymentNote',
        user_id: payload.userId,
        description: payload.notes,
      });
    }

    if (from_type === 'Imobiliária') {
      const company = yield select(getCompany);
      payload.id = apiPaymentInfo.data.result
        && apiPaymentInfo.data.result.billing_info
        && apiPaymentInfo.data.result.billing_info.id;
      payload.company_id = company.id;
      yield put(LocalCreators.editLocalRequest(payload));
    }
    yield put(TasksCreators.setLoading(false));

    yield all([
      put(TasksCreators.taskPaymentInfoSuccess(apiPaymentInfo.data.result)),
      put(
        UiCreators.growl(
          'Informações de pagamento salvas com sucesso!',
          growlTypes.GROWL_SUCCESS,
        ),
      ),
      put(UiCreators.hideLoader('PAYMENT_INFO')),
    ]);
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(TasksCreators.setLoading(false));
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/paymentInfo`,
        }),
      ),
      put(UiCreators.hideLoader('PAYMENT_INFO')),
    ]);
  }
}

export function* setTaskIsNeedVisit({ payload }) {
  try {
    const {
      id, task_id, isNeedVisit, isDetailTask, isSendWhats,
    } = payload;

    yield put(UiCreators.showLoader('SET_TASK_VISITED_IS_NEED_VISIT'));
    yield call(updateTaskVisited, task_id, isNeedVisit);

    let result;
    if (isNeedVisit) {
      // criar tabela de visita
      const params = { task_id, isSendWhats };
      result = yield call(createTaskVisit, params);
      if (isDetailTask) {
        yield put(TasksCreators.editDetailTaskVisitSucess(result.taskVisit));
      } else {
        yield put(TasksCreators.editTaskVisitSucess(result.taskVisit));
      }
    } else {
      // remover tabela de visita
      result = yield call(deleteTaskVisit, id);

      const nullVisit = {
        task_id,
        id: null,
        scheduling_date: null,
        is_active: null,
        status: null,
      };

      if (isDetailTask) {
        yield put(TasksCreators.editDetailTaskVisitSucess({
          nullVisit,
        }));
      } else {
        yield put(
          TasksCreators.editTaskVisitSucess({
            nullVisit,
          }),
        );
      }
    }

    if (isDetailTask) {
      yield put(TasksCreators.setDetailTaskIsNeededVisitSuccess(payload));
    } else {
      yield put(TasksCreators.setTaskIsNeededVisitSuccess(payload));
    }

    yield put(UiCreators.hideLoader('SET_TASK_VISITED_IS_NEED_VISIT'));

    yield put(
      UiCreators.growl('Pedido alterado com sucesso!', growlTypes.GROWL_SUCCESS),
    );
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/setTaskIsNeedVisit`,
        }),
      ),
      put(UiCreators.hideLoader('SET_TASK_VISITED_IS_NEED_VISIT')),
    ]);
  }
}

export function* setTaskVisit({ payload }) {
  try {
    yield put(UiCreators.showLoader('SET_TASK_VISIT'));
    const { isDetailTask } = payload;
    delete payload.isDetailTask;

    yield call(editTaskVisit, payload);

    if (isDetailTask) {
      yield put(TasksCreators.editDetailTaskVisitSucess(payload));
    } else {
      yield put(TasksCreators.editTaskVisitSucess(payload));
    }

    yield put(UiCreators.hideLoader('SET_TASK_VISIT'));
    yield put(
      UiCreators.growl('Data agendada com Sucesso!', growlTypes.GROWL_SUCCESS),
    );
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/setTaskVisit`,
        }),
      ),
      put(UiCreators.hideLoader('SET_TASK_VISIT')),
    ]);
  }
}

export function* getComments({ payload }) {
  try {
    yield put(UiCreators.showLoader('GET_COMMENTS_REQUEST'));

    const data = yield call(apiGetComments, payload.taskId);
    const commentsSize = data.activities
      ? data.activities.filter(
        activity => activity.type === 'comment' && activity.is_visible_company,
      ).length
      : 0;

    yield put(
      TasksCreators.getCommentsSuccess({
        comments:
          data.activities && data.activities.sort((a, b) => b.id - a.id),
        commentsSize,
      }),
    );

    yield put(UiCreators.hideLoader('GET_COMMENTS_REQUEST'));
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/getComments`,
        }),
      ),
      put(UiCreators.hideLoader('GET_COMMENTS_REQUEST')),
    ]);
  }
}

export function* includeComment({ payload }) {
  try {
    yield put(UiCreators.showLoader('INCLUDE_COMMENTS_REQUEST'));

    yield call(apiIncludeComment, payload.taskId, payload.comment);

    yield put(TasksCreators.getCommentsRequest({ taskId: payload.taskId }));

    yield put(UiCreators.hideLoader('INCLUDE_COMMENTS_REQUEST'));
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/includeComments`,
        }),
      ),
      put(UiCreators.hideLoader('INCLUDE_COMMENTS_REQUEST')),
    ]);
  }
}

export function* setOperatorTask({ payload }) {
  try {
    yield put(UiCreators.showLoader('SET_OPERATOR_TASK'));
    const { operator_id, task_id, ofList = true } = payload;
    if (operator_id) {
      yield call(linkUserOperator, task_id, operator_id);
    } else {
      yield call(unlinkUserOperator, task_id);
    }
    yield put(ofList ? TasksCreators.setTaskOperatorSuccess({ task_id, operator_id })
      : TasksCreators.setTaskOperatorSuccessDetail({ task_id, operator_id }));

    yield put(UiCreators.hideLoader('SET_OPERATOR_TASK'));
    yield put(
      UiCreators.growl(
        'Operador atualizado com sucesso!',
        growlTypes.GROWL_SUCCESS,
      ),
    );
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/setOperatorTask`,
        }),
      ),
      put(UiCreators.hideLoader('SET_OPERATOR_TASK')),
    ]);
  }
}

export function* includeLabelTask({ payload }) {
  try {
    yield put(UiCreators.showLoader('INCLUDE_LABEL_TASK'));
    const { task_id, label_id, isSendWhats } = payload;
    yield call(createTaskLabel, task_id, label_id, { isSendWhats });

    yield put(TasksCreators.includeLabelsTaskSuccess(payload));

    yield put(UiCreators.hideLoader('INCLUDE_LABEL_TASK'));
    yield put(
      UiCreators.growl(
        'Tag adicionada com sucesso!',
        growlTypes.GROWL_SUCCESS,
      ),
    );
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/includeLabelTask`,
        }),
      ),
      put(UiCreators.hideLoader('INCLUDE_LABEL_TASK')),
    ]);
  }
}

export function* deleteLabelTask({ payload }) {
  try {
    yield put(UiCreators.showLoader('DELETE_LABEL_TASK'));
    const { task_id, label_id } = payload;
    yield call(deleteTaskLabel, task_id, label_id);

    yield put(TasksCreators.removeLabelsTaskSuccess(payload));

    yield put(UiCreators.hideLoader('DELETE_LABEL_TASK'));
    yield put(
      UiCreators.growl(
        'Tag removida com sucesso!',
        growlTypes.GROWL_SUCCESS,
      ),
    );
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/deleteLabelTask`,
        }),
      ),
      put(UiCreators.hideLoader('DELETE_LABEL_TASK')),
    ]);
  }
}

export function* generateCheckoutLinkSaga({ payload }) {
  try {
    yield put(UiCreators.showLoader('GENERATE_CHECKOUT_LINK'));

    const { user, task_id, quote_id } = payload;

    if (!user.id) {
      const { user: { id } } = yield call(createUser, user);
      Object.assign(user, { id });
    } else {
      yield call(updateUser, user);
    }

    const { id: user_id } = user;
    const data = yield call(generateCheckoutLink, { user_id, task_id, quote_id });

    yield put(TasksCreators.setCheckoutParams(data));

    yield all([
      put(UiCreators.hideLoader('GENERATE_CHECKOUT_LINK')),
      put(UiCreators.growl(
        'Link para pagamento criado com sucesso!',
        growlTypes.GROWL_SUCCESS,
      )),
    ]);
  } catch (err) {
    const message = getErrorMessage(err);

    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          err,
          method: `${fileName}/generateCheckoutLink`,
        }),
      ),
      put(UiCreators.hideLoader('GENERATE_CHECKOUT_LINK')),
    ]);
  }
}
